import type { paths } from "@src/packages/openapi/schema";

type CreatePhaseBody = NonNullable<
  paths["/{tenantId}/cycles/{id}/phases"]["post"]["requestBody"]
>["content"]["application/json"];

export type PhaseEmbryoTransferInfoBody = NonNullable<
  CreatePhaseBody["embryoTransferInfo"]
>;

type TransferInfoData = Record<string, string | number | boolean | null>;

/**
 * Build the writable embryo-transfer details from the transfer form while
 * carrying forward any clinic-reported fields already on the phase.
 *
 * The patient form currently owns only embryo type and notes. The remaining
 * fields are copied from the generated API response so a patient update does
 * not erase procedure details entered by a clinic.
 */
export function buildEmbryoTransferInfo(
  data: TransferInfoData,
  existing?: PhaseEmbryoTransferInfoBody | null,
): PhaseEmbryoTransferInfoBody | undefined {
  const hasEmbryoType = Object.prototype.hasOwnProperty.call(
    data,
    "embryoType",
  );
  const hasNotes = Object.prototype.hasOwnProperty.call(data, "notes");
  if (!hasEmbryoType && !hasNotes) return undefined;

  const embryoType = hasEmbryoType
    ? data.embryoType === "fresh" || data.embryoType === "frozen"
      ? data.embryoType
      : null
    : (existing?.embryoType ?? null);
  const notes = hasNotes
    ? typeof data.notes === "string"
      ? data.notes
      : null
    : (existing?.notes ?? null);

  return {
    ...(existing?.id ? { id: existing.id } : {}),
    catheterType: existing?.catheterType ?? null,
    loadingMethod: existing?.loadingMethod ?? null,
    mediumCoverage: existing?.mediumCoverage ?? null,
    loadingVolumeMl: existing?.loadingVolumeMl ?? null,
    transferMedium: existing?.transferMedium ?? null,
    incubationTimeMinutes: existing?.incubationTimeMinutes ?? null,
    catheterBrand: existing?.catheterBrand ?? null,
    isAssistedHatching: existing?.isAssistedHatching ?? null,
    otherLabAddons: existing?.otherLabAddons ?? null,
    isMucusInCatheter: existing?.isMucusInCatheter ?? null,
    isBloodOnInnerCatheter: existing?.isBloodOnInnerCatheter ?? null,
    isUltrasoundGuidanceUsed: existing?.isUltrasoundGuidanceUsed ?? null,
    endometrialThicknessMm: existing?.endometrialThicknessMm ?? null,
    isAnesthesiaUsed: existing?.isAnesthesiaUsed ?? null,
    transferDifficulty: existing?.transferDifficulty ?? null,
    isEmbryoRetained: existing?.isEmbryoRetained ?? null,
    embryoType,
    notes,
  } satisfies PhaseEmbryoTransferInfoBody;
}
